import { useState } from 'react';
import { useParams, Link, useSearch } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  MapPin,
  Globe,
  Mail,
  ChevronLeft,
  Phone,
  Clock,
  Instagram,
  Facebook,
} from 'lucide-react';
import type { CalendarListResponseDto } from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, fetchJson } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PublicShell } from '@/components/public-shell';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import { AutoRedirect } from '@/components/auto-redirect';
import { EntryCard, formatGroupHeader, getEntryDayKey } from '@/components/calendar/entry-card';
import { EventDetailSheet } from '@/components/calendar/event-detail-sheet';

const isLogoUrl = (url: string) => url.includes('assets.kalovate.com');
const DEFAULT_TIMEZONE = 'America/Indiana/Indianapolis';
const VENUE_AGENDA_DAYS = 90;

interface LocationDetail {
  id: string;
  name: string;
  slug: string;
  localityId: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  description: string | null;
  website: string | null;
  phoneNumber: string | null;
  email: string | null;
  hours: string | null;
  thumbnailUrl: string | null;
  socialLinks: { instagram?: string; facebook?: string } | null;
}

interface ClassRow {
  id: string;
  title: string;
  description: string | null;
  price: string;
  pricingMode: string;
  thumbnailUrl: string | null;
  creatorId: string | null;
  deliveryMode: string;
  nextSessionStart?: string | null;
  nextSessionEnd?: string | null;
}

interface CreatorRow {
  id: string;
  displayName: string;
  bio: string | null;
  profileImageUrl: string | null;
}

interface LocationDetailResponse {
  location: LocationDetail;
  classes: ClassRow[];
  creators: CreatorRow[];
  timezone: string;
}

export default function LocationDetailPage() {
  const { regionSlug, localitySlug, locationSlug } = useParams<{
    regionSlug: string;
    localitySlug?: string;
    locationSlug: string;
  }>();
  const search = useSearch();
  const returnToParam = new URLSearchParams(search).get('returnTo');
  const locationsHref = returnToParam
    ? decodeURIComponent(returnToParam)
    : localitySlug
      ? `/regions/${regionSlug}/${localitySlug}/locations`
      : `/regions/${regionSlug}/locations`;

  const apiUrl = localitySlug
    ? `/api/regions/${regionSlug}/${localitySlug}/locations/${locationSlug}`
    : `/api/regions/${regionSlug}/locations/${locationSlug}`;

  const [openEventId, setOpenEventId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<LocationDetailResponse>({
    queryKey: [apiUrl],
    queryFn: () => fetchJson<LocationDetailResponse>(apiUrl),
    // Retry transient errors (cold-start 5xx) but not a genuine 404.
    retry: (failureCount, err) => {
      const status = (err as Error & { status?: number }).status;
      if (status === 404) return false;
      return failureCount < 3;
    },
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 5000),
  });

  // Venue agenda — same calendar system as the main region calendar
  // (sessions + events + recurring instances), scoped to this venue via the
  // API's existing venueSlug filter.
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const venueTimezone = data?.timezone ?? DEFAULT_TIMEZONE;

  const agendaQueryKey = ['venue-calendar-agenda', regionSlug, locationSlug, venueTimezone];
  const { data: agendaResponse, isLoading: agendaLoading } = useQuery<CalendarListResponseDto>({
    queryKey: agendaQueryKey,
    queryFn: () => {
      const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: venueTimezone });
      const dateTo = new Date();
      dateTo.setDate(dateTo.getDate() + VENUE_AGENDA_DAYS);
      const params = new URLSearchParams({
        venueSlug: locationSlug,
        dateFrom: todayStr,
        dateTo: dateTo.toLocaleDateString('en-CA', { timeZone: venueTimezone }),
        limit: '50',
      });
      return authedJson<CalendarListResponseDto>(
        `/api/regions/${regionSlug}/calendar?${params.toString()}`
      );
    },
    enabled: !!regionSlug && !!locationSlug && !!data,
    staleTime: 1000 * 60 * 5,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ eventId, saved }: { eventId: string; saved: boolean }) => {
      await apiRequest(saved ? 'DELETE' : 'POST', `/api/calendar/events/${eventId}/save`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agendaQueryKey }),
  });

  const rsvpMutation = useMutation({
    mutationFn: async ({
      eventId,
      status,
    }: {
      eventId: string;
      status: 'going' | 'interested' | null;
    }) => {
      await apiRequest(
        status === null ? 'DELETE' : 'POST',
        `/api/calendar/events/${eventId}/rsvp`,
        status !== null ? { status } : undefined
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agendaQueryKey }),
  });

  function rawId(entryId: string): string {
    return entryId.replace(/^event-/, '');
  }

  function handleSaveToggle(rawEventId: string) {
    if (!isAuthenticated) {
      toast({
        title: 'Log in to save events',
        description: 'Create an account to bookmark events and get reminders.',
      });
      return;
    }
    const currentlySaved = agendaResponse?.savedEventIds?.includes(rawEventId) ?? false;
    saveMutation.mutate({ eventId: rawEventId, saved: currentlySaved });
  }

  function handleRsvp(rawEventId: string, status: 'going' | 'interested') {
    if (!isAuthenticated) {
      toast({
        title: 'Log in to RSVP',
        description: 'Create an account to mark events you plan to attend.',
      });
      return;
    }
    rsvpMutation.mutate({ eventId: rawEventId, status });
  }

  function handleCancelRsvp(rawEventId: string) {
    rsvpMutation.mutate({ eventId: rawEventId, status: null });
  }

  if (isLoading) {
    return (
      <PublicShell routeKey="location-detail">
        <div className="page-shell py-12 text-center text-muted-foreground">Loading…</div>
      </PublicShell>
    );
  }

  if (error || !data) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-muted-foreground">Location not found.</p>
          <AutoRedirect />
        </div>
      </PublicShell>
    );
  }

  const { location, classes, creators } = data;
  const addressLine = [location.address, location.city, location.state, location.zip]
    .filter(Boolean)
    .join(', ');

  const venueAgendaGroups = (() => {
    const groups = new Map<string, CalendarListResponseDto['data']>();
    for (const entry of agendaResponse?.data ?? []) {
      const dayKey = getEntryDayKey(entry);
      if (!groups.has(dayKey)) groups.set(dayKey, []);
      groups.get(dayKey)?.push(entry);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  })();

  return (
    <PublicShell routeKey="location-detail">
      <div className="page-shell py-8">
        <Link
          href={locationsHref}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Locations
        </Link>

        {location.thumbnailUrl &&
          (isLogoUrl(location.thumbnailUrl) ? (
            <div className="border rounded-xl mb-6 bg-white dark:bg-zinc-900 h-56 px-3">
              <img
                src={normalizeImageUrl(location.thumbnailUrl, PLACEHOLDER.thumb)}
                alt={location.name}
                className="w-full h-full object-contain"
                onError={imageErrorFallback(PLACEHOLDER.thumb, location.name)}
              />
            </div>
          ) : (
            <div className="relative h-56 w-full overflow-hidden rounded-xl mb-6">
              <img
                src={normalizeImageUrl(location.thumbnailUrl, PLACEHOLDER.thumb)}
                alt={location.name}
                className="w-full h-full object-cover"
                onError={imageErrorFallback(PLACEHOLDER.thumb, location.name)}
              />
            </div>
          ))}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl font-bold">{location.name}</h1>
              {addressLine && (
                <p className="flex items-center gap-1 text-muted-foreground mt-1">
                  <MapPin className="h-4 w-4 shrink-0" />
                  {addressLine}
                </p>
              )}
            </div>

            {location.description && (
              <div>
                <h2 className="text-lg font-semibold mb-2">About this space</h2>
                <p className="text-muted-foreground whitespace-pre-line">{location.description}</p>
              </div>
            )}

            {classes.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Classes here</h2>
                <div className="space-y-3">
                  {classes.map(cls => (
                    <Link key={cls.id} href={`/classes/${cls.id}`}>
                      <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                        <CardContent className="p-4 flex gap-4 items-start">
                          {cls.thumbnailUrl && (
                            <img
                              src={normalizeImageUrl(cls.thumbnailUrl, PLACEHOLDER.thumb)}
                              alt={cls.title}
                              className="w-16 h-16 rounded-md object-cover shrink-0"
                              onError={imageErrorFallback(PLACEHOLDER.thumb, cls.title)}
                            />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{cls.title}</p>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                              {cls.description}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                {cls.pricingMode === 'free'
                                  ? 'Free'
                                  : `$${parseFloat(cls.price).toFixed(2)}`}
                              </Badge>
                              {cls.nextSessionStart && (
                                <span className="text-xs text-muted-foreground">
                                  Next:{' '}
                                  {new Date(cls.nextSessionStart).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {creators.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Creators at this space</h2>
                <div className="grid grid-cols-2 gap-3">
                  {creators.map(creator => (
                    <Link key={creator.id} href={`/creators/${creator.id}`}>
                      <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                        <CardContent className="p-4 flex items-center gap-3">
                          {creator.profileImageUrl ? (
                            <img
                              src={normalizeImageUrl(creator.profileImageUrl, PLACEHOLDER.avatar)}
                              alt={creator.displayName}
                              className="w-10 h-10 rounded-full object-cover shrink-0"
                              onError={imageErrorFallback(PLACEHOLDER.avatar, creator.displayName)}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 text-lg font-semibold">
                              {creator.displayName[0]}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{creator.displayName}</p>
                            {creator.bio && (
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {creator.bio}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-semibold">What's happening here</h2>
              </div>

              {agendaLoading ? (
                <div className="grid gap-4">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-28 animate-pulse rounded-[1.75rem] border border-border/70 bg-card"
                    />
                  ))}
                </div>
              ) : venueAgendaGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No upcoming classes or events scheduled at this venue yet.
                </p>
              ) : (
                <div className="space-y-6">
                  {venueAgendaGroups.map(([dayKey, dayEntries]) => (
                    <div key={dayKey}>
                      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                        {formatGroupHeader(dayKey, venueTimezone)}
                      </h3>
                      <div className="grid gap-4">
                        {dayEntries.map(entry => {
                          const rid = rawId(entry.id);
                          const counts = agendaResponse?.rsvpCounts?.[rid];
                          return (
                            <EntryCard
                              key={entry.id}
                              entry={entry}
                              basePath={`/regions/${regionSlug}`}
                              onOpen={() => setOpenEventId(entry.id)}
                              isSaved={agendaResponse?.savedEventIds?.includes(rid) ?? false}
                              onSave={() => handleSaveToggle(rid)}
                              rsvpStatus={agendaResponse?.userRsvpMap?.[rid] ?? null}
                              onRsvp={status => handleRsvp(rid, status)}
                              onCancelRsvp={() => handleCancelRsvp(rid)}
                              goingCount={counts?.going ?? 0}
                              interestedCount={counts?.interested ?? 0}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                {location.website && (
                  <a
                    href={
                      location.website.startsWith('http')
                        ? location.website
                        : `https://${location.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Globe className="h-4 w-4 shrink-0" />
                    {location.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {location.phoneNumber && (
                  <a
                    href={`tel:${location.phoneNumber}`}
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Phone className="h-4 w-4 shrink-0" />
                    {location.phoneNumber}
                  </a>
                )}
                {location.email && (
                  <a
                    href={`mailto:${location.email}`}
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    {location.email}
                  </a>
                )}
                {(location.socialLinks?.instagram || location.socialLinks?.facebook) && (
                  <div className="flex items-center gap-3 pt-1">
                    {location.socialLinks?.instagram && (
                      <a
                        href={location.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Instagram className="h-4 w-4" />
                      </a>
                    )}
                    {location.socialLinks?.facebook && (
                      <a
                        href={location.socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Facebook className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {location.hours && (
              <Card>
                <CardContent className="p-4 space-y-2 text-sm">
                  <p className="flex items-center gap-2 font-medium">
                    <Clock className="h-4 w-4 shrink-0" />
                    Hours
                  </p>
                  <p className="text-muted-foreground whitespace-pre-line">{location.hours}</p>
                </CardContent>
              </Card>
            )}

            {addressLine && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(addressLine)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4 flex items-center gap-2 text-sm text-primary">
                    <MapPin className="h-4 w-4" />
                    View on Google Maps
                  </CardContent>
                </Card>
              </a>
            )}
          </aside>
        </div>
      </div>

      <EventDetailSheet
        entryId={openEventId}
        onOpenChange={open => {
          if (!open) setOpenEventId(null);
        }}
      />
    </PublicShell>
  );
}
